/* eslint-disable no-console */
/* eslint-disable max-len */
import type { Environment } from '../environment';
import type {
  EntityKey,
  EntityObject,
  OverreactRequestShape,
  ResponseType,
  StoredEntity,
} from '../public-types';
import { OVERREACT_ID_FIELD_NAME, createOverreactId } from '../store/consts';
import type { RequestContract } from './request-contract';
import { specTypes } from './spec-types';
import errorResponseHandler from './response-handler/error';
import fetchResponseHandler from './response-handler/fetch';
import mutationResponseHandler from './response-handler/mutation';
import refetchResponseHandler from './response-handler/refetch';

export interface ResponseContractOptions<
  TVariables extends object,
  TData,
  TEntity extends EntityObject,
  TNetworkResponse,
  TResult extends TEntity | TEntity[] | null,
> {
  requestContract: RequestContract<TVariables, TData>;
  responseType: ResponseType;
  keySelector: (_entity: TEntity) => EntityKey;
  processorFn?: (_response: TNetworkResponse, _request: OverreactRequestShape<TVariables, TData, TResult, TEntity>) => TResult;
  errorConvertFn?: (_error: unknown) => unknown;
}

export class ResponseContract<
  TVariables extends object = object,
  TData = unknown,
  TEntity extends EntityObject = EntityObject,
  TNetworkResponse = unknown,
  TResult extends TEntity | TEntity[] | null = TEntity,
> {
  errorConvertFn?: (_error: unknown) => unknown;
  keySelector: (_entity: TEntity) => EntityKey;
  processorFn?: (_response: TNetworkResponse, _request: OverreactRequestShape<TVariables, TData, TResult, TEntity>) => TResult;
  requestContract: RequestContract<TVariables, TData>;
  responseType: ResponseType;
  schemaNode: ReturnType<RequestContract<TVariables, TData>['getSchemaNode']>;

  constructor(options: ResponseContractOptions<TVariables, TData, TEntity, TNetworkResponse, TResult>) {
    Object.assign(this, options);
    this.schemaNode = this.requestContract.getSchemaNode();

    this.selectKey = this.selectKey.bind(this);
    this.onGetResponse = this.onGetResponse.bind(this);
    this.onGetError = this.onGetError.bind(this);
    this.applyId = this.applyId.bind(this);
  }

  selectKey(entity: TEntity) {
    return this.keySelector(entity);
  }

  applyId(entity: TEntity, parentId: EntityKey | undefined): StoredEntity<TEntity> {
    const overreactId = createOverreactId(
      this.keySelector(entity),
      parentId,
      this.schemaNode.parentNode?.name ?? null,
    );
    return {
      rawData: entity,
      [OVERREACT_ID_FIELD_NAME]: overreactId,
    };
  }

  onGetResponse(
    environment: Environment,
    response: unknown,
    request: OverreactRequestShape<TVariables, TData, TResult, TEntity>,
  ) {
    const processedResponse = !request.middlewareStates?.isResponseFromStore && this.processorFn
      ? this.processorFn(response as TNetworkResponse, request)
      : response as unknown as TResult;

    switch (request.spec.specType) {
      case specTypes.FETCH:
      case specTypes.PAGINATION:
        return fetchResponseHandler(environment, processedResponse, request)(this);
      case specTypes.ADD:
      case specTypes.DELETE:
      case specTypes.MUTATION:
        return mutationResponseHandler(environment, processedResponse, request)(this);
      case specTypes.REFETCH:
        return refetchResponseHandler(environment, processedResponse, request)(this);
      default:
        return null;
    }
  }

  onGetError(
    environment: Environment,
    request: OverreactRequestShape<TVariables, TData, TResult, TEntity>,
    error: unknown,
  ) {
    return errorResponseHandler(environment, request, error)(this);
  }
}

export function createResponseContract<
  TVariables extends object = object,
  TData = unknown,
  TEntity extends EntityObject = EntityObject,
  TNetworkResponse = TEntity,
  TResult extends TEntity | TEntity[] | null = TEntity,
>(options: ResponseContractOptions<TVariables, TData, TEntity, TNetworkResponse, TResult>) {
  return new ResponseContract(options);
}
